import { List, X } from '@phosphor-icons/react';
import { Dialog } from 'radix-ui';
import { useState, type ComponentProps, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router';
import { LogoMark } from '@/components/brand';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import type { NavGroup } from './nav';

/** Marca no topo da sidebar (compartilhada entre o layout do tenant e o da plataforma). */
export function Brand({ to = '/' }: { to?: string }) {
  return (
    <Link to={to} className="flex items-center gap-2.5 rounded-control px-2 py-1 font-display text-lg font-semibold tracking-tight text-white dark:text-sidebar-foreground">
      <LogoMark />
      BrechôFlow
    </Link>
  );
}

/** Grupos de navegação da sidebar, com rótulo de grupo e item de menu padrão. */
export function SidebarGroups({ groups, label = 'Principal', onNavigate }: { groups: NavGroup[]; label?: string; onNavigate?: () => void }) {
  return (
    <nav aria-label={label} className="flex flex-col gap-5">
      {groups.map((g, gi) => (
        <div key={gi} className="flex flex-col gap-0.5">
          {g.label && <p className="px-3 pb-1 text-xs font-medium text-sidebar-muted">{g.label}</p>}
          {g.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'group flex min-h-10 items-center gap-3 rounded-control px-3 text-sm font-medium transition-colors',
                  isActive ? 'bg-sidebar-active text-white dark:text-sidebar-foreground' : 'text-sidebar-foreground/85 hover:bg-sidebar-active/60 hover:text-white dark:hover:text-sidebar-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon className={cn('size-[18px] shrink-0', isActive ? 'text-sidebar-accent' : 'text-sidebar-muted group-hover:text-sidebar-accent')} weight={isActive ? 'fill' : 'regular'} aria-hidden />
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

/** Botão que abre o drawer de navegação no mobile. */
export function MenuButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="ghost" size="icon" className="lg:hidden" onClick={onClick} aria-label="Abrir menu">
      <List className="size-5" aria-hidden />
    </Button>
  );
}

/** Casca grid + drawer mobile + coluna principal. Usada pelo AppLayout e pelo PlatformLayout. */
export function Shell({
  brandTo,
  menuTitle,
  menuDescription,
  nav,
  footer,
  topBar,
  beforeMain,
  banner,
  testId,
  children,
}: {
  brandTo?: string;
  menuTitle: string;
  menuDescription: string;
  nav: (onNavigate?: () => void) => ReactNode;
  footer: (onNavigate?: () => void) => ReactNode;
  topBar: (onMenu: () => void) => ReactNode;
  beforeMain?: ReactNode;
  banner?: ReactNode;
  testId?: string;
  children: ReactNode;
}) {
  const [drawer, setDrawer] = useState(false);
  const close = () => setDrawer(false);
  return (
    <>
      {banner}
      <div className="min-h-[calc(100dvh-var(--imp-h,0px))] lg:pl-64" data-testid={testId}>
        {/* sidebar FIXA na janela (não depende da altura do contêiner nem da rolagem da página): marca e rodapé ficam parados;
            só a lista de itens rola (por dentro, sem barra visível) quando a janela é baixa. Desce abaixo da faixa de impersonação. */}
        <aside className="fixed bottom-0 left-0 top-[var(--imp-h,0px)] z-30 hidden w-64 flex-col gap-4 overflow-hidden bg-sidebar px-3 py-4 lg:flex" data-testid="sidebar">
          <div className="shrink-0">
            <Brand to={brandTo} />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">{nav()}</div>
          <div className="flex shrink-0 flex-col">{footer()}</div>
        </aside>

        <Dialog.Root open={drawer} onOpenChange={setDrawer}>
          <Dialog.Portal>
            <Dialog.Overlay className="oc-fade-in fixed inset-0 z-50 bg-[oklch(0.2_0.03_205/0.55)] lg:hidden" />
            <Dialog.Content className="oc-slide-in fixed top-[var(--imp-h,0px)] bottom-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col gap-6 overflow-y-auto bg-sidebar px-3 py-5 lg:hidden">
              <Dialog.Title className="sr-only">{menuTitle}</Dialog.Title>
              <Dialog.Description className="sr-only">{menuDescription}</Dialog.Description>
              <div className="flex items-center justify-between">
                <Brand to={brandTo} />
                <Dialog.Close aria-label="Fechar menu" className="flex size-10 items-center justify-center rounded-control text-sidebar-foreground hover:bg-sidebar-active">
                  <X className="size-5" aria-hidden />
                </Dialog.Close>
              </div>
              {nav(close)}
              {footer(close)}
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        <div className="flex min-w-0 flex-col">
          {topBar(() => setDrawer(true))}
          {beforeMain}
          <main id="conteudo" className="mx-auto w-full min-w-0 max-w-[84rem] flex-1 px-4 py-6 sm:px-8 sm:py-8">
            {children}
          </main>
        </div>
      </div>
    </>
  );
}

/** Cabeçalho fixo de 4rem; o conteúdo à esquerda e à direita é montado por quem usa. */
export function TopBarFrame({ onMenu, children }: { onMenu: () => void; children: ReactNode }) {
  return (
    <header className="sticky top-[var(--imp-h,0px)] z-30 flex h-16 items-center gap-2 border-b bg-background/90 px-3 backdrop-blur-sm sm:px-6">
      <MenuButton onClick={onMenu} />
      {children}
    </header>
  );
}

/** Gatilho do menu de usuário (avatar com iniciais + nome). */
export function UserMenuTrigger({ name, initialsText, ...props }: { name: string; initialsText: string } & Omit<ComponentProps<typeof Button>, 'children' | 'variant' | 'className'>) {
  return (
    <Button {...props} variant="ghost" className="gap-2.5 px-1.5 sm:px-2" aria-label="Menu do usuário">
      <span className="flex size-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">{initialsText}</span>
      <span className="hidden max-w-40 truncate text-sm sm:block">{name}</span>
    </Button>
  );
}

/** Bloco de identidade no topo do menu de usuário. */
export function UserMenuHeader({ name, email, role }: { name: string; email: string; role: string }) {
  return (
    <div className="px-3 py-2">
      <p className="truncate text-sm font-semibold">{name}</p>
      <p className="truncate text-xs text-muted-foreground">{email}</p>
      <p className="mt-0.5 truncate text-xs text-muted-foreground">{role}</p>
    </div>
  );
}
